<template>
  <v-container fluid>
    <!-- Header con título y controles -->
   
      
      <!-- Controles de filtro -->
    
        <v-row>
          <v-col cols="6" md="3">
            <v-autocomplete
              v-model="selectedYear"
              :items="availableYears"
              label="Año"
              outlined
              dense
              prepend-inner-icon="fa-solid fa-calendar"
              @change="loadData"
              :disabled="loading"
            ></v-autocomplete>
          </v-col>
          <v-col cols="6" md="3">
            <v-autocomplete
              v-model="selectedLevel"
              :items="availableLevels"
              label="Nivel"
              outlined
              dense
              prepend-inner-icon="fa-solid fa-sitemap"
              @change="loadData"
              :disabled="loading"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchText"
              label="Buscar colaborador, código o cargo"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-magnify"
              :disabled="loading"
            ></v-text-field>
          </v-col>
         
         
        </v-row>
      

    <!-- Tarjeta principal -->
    <v-card elevation="2">
      
      <v-card-title class="pb-2">
        <v-icon class="mr-2">mdi-table</v-icon>
        Resumen de Vacaciones {{ selectedYear }}
        <v-spacer></v-spacer>
        <v-chip v-if="!loading" color="primary" outlined>
          {{ filteredEmployees.length }} {{ filteredEmployees.length === 1 ? 'colaborador' : 'colaboradores' }}
        </v-chip>
        <v-skeleton-loader v-else type="chip" width="120"></v-skeleton-loader>
      </v-card-title>

      <!-- Tabla de vacaciones -->
      <v-card-text class="pa-0">

        <!-- Skeleton loader mientras carga -->
        <div v-if="loading" class="pa-4">
          <!-- Skeleton de headers -->
          <v-skeleton-loader type="table-heading" class="mb-2"></v-skeleton-loader>
          <!-- Skeleton de filas -->
          <v-skeleton-loader
            v-for="n in 8"
            :key="'skeleton-' + n"
            type="table-row"
            class="mb-1"
          ></v-skeleton-loader>
          <!-- Skeleton de paginación -->
          <div class="d-flex justify-end pa-2">
            <v-skeleton-loader type="text" width="200"></v-skeleton-loader>
          </div>
        </div>

        <!-- Estado vacío cuando no hay colaboradores -->
        <div v-else-if="filteredEmployees.length === 0" class="text-center pa-6">
          <v-icon size="64" color="grey" class="mb-3">mdi-account-search</v-icon>
          <div class="text-h6 grey--text mb-2">No se encontraron colaboradores</div>
          <div class="text-body-2 grey--text">
            <div v-if="searchText">
              No hay colaboradores que coincidan con "<strong>{{ searchText }}</strong>"
            </div>
            <div v-else>
              No hay datos de vacaciones disponibles para {{ selectedYear }}
            </div>
          </div>
          <v-btn 
            v-if="!searchText" 
            color="primary" 
            outlined 
            class="mt-3"
            @click="loadData"
            :loading="loading"
          >
            <v-icon left>mdi-refresh</v-icon>
            Reintentar carga
          </v-btn>
        </div>
        
        <!-- Tabla principal -->
        <v-data-table
          v-else
          :headers="tableHeaders"
          :items="filteredEmployees"
          :search="searchText"
          :expanded.sync="expanded"
          show-expand
          item-key="id"
          class="elevation-0"
          hide-default-header
          mobile-breakpoint="0"
          :items-per-page="20"
          @item-expanded="onItemExpanded"
        >
          <!-- Slot para headers clicables de meses -->
          <template slot="header" slot-scope="{ props }">
            <thead class="v-data-table-header">
              <tr>
                <th 
                  v-for="header in props.headers" 
                  :key="header.value"
                  :class="[
                    'text-start', 
                    header.align ? `text-${header.align}` : '',
                    header.month ? 'clickable-month-header' : ''
                  ]"
                  :style="{ width: header.width }"
                  @click="header.month ? openMonthDialog(header.month, header.monthIndex, header.text) : null"
                >
                  <span v-if="header.month" class="month-header-content">
                    {{ header.text }}
                    <v-icon small class="ml-1">mdi-calendar-month</v-icon>
                  </span>
                  <span v-else>{{ header.text }}</span>
                </th>
              </tr>
            </thead>
          </template>

          <!-- Slot para el nombre del colaborador -->
          <template slot="item.nombre" slot-scope="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" color="primary" class="mr-3">
                <span class="white--text font-weight-bold">
                  {{ item.nombre.charAt(0) }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.nombre }}</div>
                <div class="text-caption grey--text mb-1">{{ item.cargo }}</div>
                <div class="d-flex align-center">
                  <v-chip x-small color="blue lighten-4" text-color="blue darken-2" class="mr-1">
                    <v-icon x-small left>fa-solid fa-id-card</v-icon>
                    {{ item.id || 'N/A' }}
                  </v-chip>
                  <v-chip x-small color="green lighten-4" text-color="green darken-2" class="mr-1">
                    <v-icon x-small left>fa-solid fa-calendar</v-icon>
                    {{ $formatoFechaSpanish(item.fechaIngreso) || 'N/A' }}
                  </v-chip>
                  
                </div>
              </div>
            </div>
          </template>

          <!-- Slots para cada mes -->
          <template slot="item.enero" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'enero'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'enero'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'enero')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'enero')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.enero)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.febrero" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'febrero'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'febrero'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'febrero')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'febrero')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.febrero)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.marzo" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'marzo'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'marzo'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'marzo')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'marzo')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.marzo)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.abril" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'abril'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'abril'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'abril')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'abril')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.abril)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.mayo" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'mayo'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'mayo'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'mayo')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'mayo')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.mayo)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.junio" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'junio'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'junio'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'junio')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'junio')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.junio)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.julio" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'julio'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'julio'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'julio')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'julio')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.julio)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.agosto" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'agosto'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'agosto'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'agosto')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'agosto')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.agosto)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.septiembre" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'septiembre'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'septiembre'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'septiembre')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'septiembre')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.septiembre)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.octubre" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'octubre'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'octubre'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'octubre')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'octubre')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.octubre)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.noviembre" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'noviembre'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'noviembre'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'noviembre')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'noviembre')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.noviembre)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <template slot="item.diciembre" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                :color="getChipColor(getTotalCombinedForMonth(item, 'diciembre'))"
                :text-color="getTextColor(getTotalCombinedForMonth(item, 'diciembre'))"
              >
                {{ formatNumber(getTotalCombinedForMonth(item, 'diciembre')) }}
              </v-chip>
              <v-icon 
                v-if="hasUnapprovedDaysInMonth(item, 'diciembre')"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.diasSinAprobarPorMes.diciembre)} días sin aprobar`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <!-- Total de días tomados -->
          <template slot="item.totalTomado" slot-scope="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip color="info" outlined>
                {{ formatNumber(item.totalCombinado) }}
              </v-chip>
              <v-icon 
                v-if="item.totalSinAprobar > 0"
                color="warning"
                size="16"
                class="ml-1"
                :title="`${formatNumber(item.totalSinAprobar)} días sin aprobar en total`"
              >
                fa-solid fa-triangle-exclamation
              </v-icon>
            </div>
          </template>

          <!-- Saldo restante -->
          <template slot="item.saldoRestante" slot-scope="{ item }">
            <v-chip
              :color="item.saldoRestante <= 0 ? 'error' : 'success'"
              :outlined="item.saldoRestante > 0"
            >
              {{ formatNumber(item.saldoRestante) }}
            </v-chip>
          </template>

          <!-- Porcentaje de uso -->
          <template slot="item.porcentajeUso" slot-scope="{ item }">
            <div class="d-flex align-center">
              <v-progress-linear
                :value="item.porcentajeUso"
                :color="getProgressColor(item.porcentajeUso)"
                height="20"
                rounded
                class="mr-2"
                style="min-width: 100px"
              >
                <template slot="default" slot-scope="{ value }">
                  <small class="white--text font-weight-bold">
                    {{ Math.ceil(value) }}%
                  </small>
                </template>
              </v-progress-linear>
            </div>
          </template>

          <!-- Fila expandible con calendario -->
          <template slot="expanded-item" slot-scope="{ item }">
            <td :colspan="tableHeaders.length" class="pa-0">
              
              <v-card flat class="ma-2">
                <v-card-title class="pb-2">
                  <v-icon class="mr-2">mdi-calendar-range</v-icon>
                  Detalle de vacaciones - {{ item.nombre }}
                </v-card-title>
                
                <v-card-text>
                  <!-- Overlay de carga de eventos -->
                  <div v-if="loadingEvents[item.id]" class="text-center pa-6">
                    <v-progress-circular
                      indeterminate
                      size="48"
                      color="primary"
                    ></v-progress-circular>
                    <div class="text-subtitle-2 grey--text mt-3">
                      Cargando detalle de vacaciones...
                    </div>
                  </div>

                  <!-- Estado vacío cuando no hay eventos -->
                  <div v-else-if="!loadingEvents[item.id] && (!employeeEventsCache[item.id] || employeeEventsCache[item.id].length === 0)" class="text-center pa-6">
                    <v-icon size="48" color="grey" class="mb-3">mdi-calendar-blank</v-icon>
                    <div class="text-subtitle-1 grey--text">Sin períodos de vacaciones registrados en {{ selectedYear }}</div>
                  </div>

                  <!-- Contenido con eventos cargados -->
                  <v-row v-else>
                    <!-- Mini calendario -->
                    <v-col cols="12" md="8">
                      <div class="calendar-container">
                        <h4 class="mb-3">Períodos de vacaciones {{ selectedYear }}</h4>
                        
                        <!-- Simulación de mini-calendario con eventos -->
                        <div class="vacation-timeline">
                          <div
                            v-for="(evento, index) in employeeEventsCache[item.id]"
                            :key="index"
                            class="vacation-event mb-2"
                          >
                            <v-chip
                              :color="evento.tipo === 'completo'? 'rgb(56, 191, 77)' : evento.tipo === 'sin_aprobar'? 'rgb(246, 185, 58)' : 'rgb(50, 108, 245)'"
                              outlined
                              class="mr-2 cursor-pointer"
                              @click="navigateToCalendarMonth(evento.start)"
                            >
                              <v-icon small left>{{ getTipoIcon(evento.tipo) }}</v-icon>
                              {{ formatDateRange(evento.start, evento.end) }}
                            </v-chip>
                            <span class="text-body-2">
                              ({{ evento.diasTomados===0?
                                  formatDays(calculateDays(evento.start, evento.end, evento.tipo, evento.diasSinAprobar)):
                                  formatDays(calculateDays(evento.start, evento.end, evento.tipo, evento.diasTomados))  
                            
                            }})
                            </span>

                              <v-icon 
                                small 
                                color="warning" 
                                v-if="evento.diasTomados === 0"
                                title="Vacaciones sin aprobar"
                              >
                                fa-solid fa-triangle-exclamation
                              </v-icon>

                          </div>
                        </div>
                        
                        <!-- Calendario visual completo -->
                        <v-divider class="my-4"></v-divider>
                        <div class="calendar-section">
                          <h4 class="subtitle-2 mb-3">
                            <v-icon small class="mr-2">mdi-calendar-month</v-icon>
                            Vista de Calendario {{ selectedYear }} - {{ item.nombre }}
                          </h4>
                          
                          <v-calendar

                            ref="calendar"
                            v-model="calendarFocus"
                            :events="getCalendarEvents(item)"
                            :event-color="getEventColor"
                            :event-margin-bottom="3"
                            :weekdays="weekday"
                            :now="today"
                            type="month"
                            
                            :event-overlap-threshold="30"
                            @click:event="showEvent"
                           
                          >
                            
                          </v-calendar>
                          
                          <!-- Leyenda del calendario -->
                          <div class="calendar-legend">
                            <v-chip 
                              small 
                             
                              class="mr-2"
                            >
                              <div class="legend-indicator primary-indicator mr-1"></div>
                              Día completo
                            </v-chip>
                            <v-chip 
                              small 
                              
                              class="mr-2"
                            >
                              <div class="legend-indicator half-indicator mr-1"></div>
                              Medio día
                            </v-chip>
                            <v-chip 
                              small 
                              
                              class="mr-2"
                            >
                              <div class="legend-indicator out-indicator mr-1"></div>
                              Sin Aprobar
                            </v-chip>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <!-- Estadísticas del colaborador -->
                    <v-col cols="12" md="4">
                      <v-card outlined>
                        <v-card-title class="pb-2">
                          <v-icon class="mr-2">mdi:mdi-chart-pie</v-icon>
                          Estadísticas
                        </v-card-title>
                        <v-card-text>
                          <div class="mb-3">
                            <div class="text-subtitle-2 grey--text">Saldo Anual</div>
                            <div class="text-h6 primary--text">{{ formatNumber(item.saldoAnual) }} días</div>
                          </div>
                          
                          <div class="mb-3">
                            <div class="text-subtitle-2 grey--text">Días Tomados</div>
                            <div class="d-flex align-center">
                              <div class="text-h6 info--text mr-2">{{ formatNumber(item.totalTomado) }} días</div>
                              
                            </div>
                          </div>

                          <div class="mb-3">
                            <div class="text-subtitle-2 grey--text">Días sin Aprobar</div>
                            <div class="d-flex align-center">
                              <div class="text-h6 warning--text mr-2">{{ formatNumber(item.totalSinAprobar) }} días</div>
                             
                            </div>
                          </div>
                          
                          <div class="mb-3">
                            <div class="text-subtitle-2 grey--text">Días Restantes</div>
                            <div class="text-h6" :class="item.saldoRestante <= 0 ? 'error--text' : 'success--text'">
                              {{ formatNumber(item.saldoRestante) }} días
                            </div>
                          </div>
                          
                          <div class="mb-3">
                            <div class="text-subtitle-2 grey--text">Períodos Tomados</div>
                            <div class="text-h6">{{ item.cantidadEventos }}</div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Footer con totales -->
      <v-divider></v-divider>
      <v-card-text class="pt-3">
        <v-row class="text-center">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon class="mr-2">mdi-chart-bar</v-icon>
              Totales por Mes
            </h3>
          </v-col>
        </v-row>
        
        <!-- Skeleton de totales por mes -->
        <v-row v-if="loading">
          <v-col
            v-for="n in 12"
            :key="'skel-mes-' + n"
            cols="6"
            sm="4"
            md="2"
            class="text-center"
          >
            <v-skeleton-loader type="chip" class="d-flex justify-center mb-2"></v-skeleton-loader>
            <v-skeleton-loader type="text" width="60" class="mx-auto"></v-skeleton-loader>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col
            v-for="(total, mes) in totalesPorMes"
            :key="mes"
            cols="6"
            sm="4"
            md="2"
            class="text-center"
          >
            <div class="mb-2">
              <v-chip
                :color="getChipColor(total)"
                :text-color="getTextColor(total)"
                class="ma-1"
              >
                {{ formatNumber(total) }}
                <v-icon 
                  v-if="mesesConDiasSinAprobar[mes]"
                  small 
                  class="ml-1" 
                  color="orange"
                >
                  fa-solid fa-triangle-exclamation
                </v-icon>
              </v-chip>
            </div>
            <div class="text-caption text-uppercase font-weight-bold">
              {{ mes }}
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Totales generales -->
        <v-row v-if="loading" class="text-center">
          <v-col v-for="n in 4" :key="'skel-total-' + n" cols="12" sm="3">
            <v-card outlined class="pa-3">
              <v-skeleton-loader type="heading" class="mb-2"></v-skeleton-loader>
              <v-skeleton-loader type="text" width="120" class="mx-auto"></v-skeleton-loader>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else class="text-center">
          <v-col cols="12" sm="3">
            <v-card outlined class="pa-3">
              <div class="text-h4 primary--text">{{ totalEmpleados }}</div>
              <div class="text-subtitle-2 grey--text">COLABORADORES</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="3">
            <v-card outlined class="pa-3">
              <div class="text-h4 info--text">{{ formatNumber(totalDiasTomados) }}</div>
              <div class="text-subtitle-2 grey--text">{{ totalDiasSinAprobar > 0 ? 'DÍAS TOMADOS Y SIN APROBAR' : 'DÍAS TOMADOS' }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="3">
            <v-card outlined class="pa-3">
              <div class="text-h4 success--text">{{ formatNumber(totalDiasDisponibles) }}</div>
              <div class="text-subtitle-2 grey--text">DÍAS DISPONIBLES</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="3">
            <v-card outlined class="pa-3">
              <div class="text-h4 warning--text">{{ formatNumber(promedioUso) }}%</div>
              <div class="text-subtitle-2 grey--text">PROMEDIO USO</div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Leyenda de colores -->
    <v-card class="mt-4" elevation="1">
      <v-card-title>
        <v-icon class="mr-2">mdi-palette</v-icon>
        Leyenda de Colores y Tipos
      </v-card-title>
      <v-card-text>
        <v-row align="center" class="mb-3">
          <v-col cols="12">
            <h4 class="mb-2">Cantidad de días por mes:</h4>
          </v-col>
          <v-col cols="auto">
            <v-chip small color="error" text-color="white" class="mr-2">0</v-chip>
            <span class="text-body-2">Sin vacaciones (crítico)</span>
          </v-col>
          <v-col cols="auto">
            <v-chip small color="warning" text-color="white" class="mr-2">1-5</v-chip>
            <span class="text-body-2">Pocos días</span>
          </v-col>
          <v-col cols="auto">
            <v-chip small color="success" text-color="white" class="mr-2">6-10</v-chip>
            <span class="text-body-2">Días moderados</span>
          </v-col>
          <v-col cols="auto">
            <v-chip small color="success" text-color="white" class="mr-2">+10</v-chip>
            <span class="text-body-2">Muchos días (óptimo)</span>
          </v-col>
          <v-col cols="auto">
            <v-icon small color="warning" text-color="white" class="mr-2">fa-solid fa-triangle-exclamation</v-icon>
            <span class="text-body-2">Vacaciones sin Aprobar</span>
          </v-col>
        </v-row>
        
        <v-divider class="my-3"></v-divider>
        
        <v-row align="center">
          <v-col cols="12">
            <h4 class="mb-2">Tipos de vacación:</h4>
          </v-col>
          <v-col cols="auto">
            <v-chip small color="primary" outlined class="mr-2">
              <v-icon small left>mdi:mdi-calendar</v-icon>
              Día completo
            </v-chip>
          </v-col>
          <v-col cols="auto">
            <v-chip small color="orange" outlined class="mr-2">
              <v-icon small left>mdi-weather-sunny</v-icon>
              Medio día
            </v-chip>
          </v-col>
          
        </v-row>
        
        <v-alert
          type="info"
          outlined
          dense
          class="mt-3"
        >
          <small>
            <v-icon small class="mr-1">mdi-information-outline</v-icon>
            Los medios días se calculan como 0.5 días. Los números decimales indican combinaciones de días completos y medios días.
          </small>
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Diálogo del calendario mensual -->
    <v-dialog 
      v-model="monthDialogVisible" 
      max-width="900px" 
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-calendar-month</v-icon>
          Vacaciones de {{ selectedMonthName }} {{ selectedYear }}
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="monthDialogVisible = false">
            <v-icon>fa-solid fa-xmark</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <div class="pa-4">
            <!-- Estadísticas del mes -->
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h5 primary--text">{{ getMonthVacationStats().totalEmployees }}</div>
                  <div class="text-caption grey--text">COLABORADORES CON VACACIONES</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h5 success--text">{{ getMonthVacationStats().totalDays }}</div>
                  <div class="text-caption grey--text">DÍAS TOTALES</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h5 info--text">{{ getMonthVacationStats().totalPeriods }}</div>
                  <div class="text-caption grey--text">PERÍODOS DE VACACIONES</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Lista de colaboradores con vacaciones en el mes -->
            <v-expansion-panels class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon left>mdi-account-group</v-icon>
                    <strong>Lista de Colaboradores ({{ getMonthVacationStats().totalEmployees }})</strong>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list dense>
                    <v-list-item 
                      v-for="employee in getEmployeesWithVacationsInMonth()" 
                      :key="employee.id"
                      class="px-0"
                    >
                      <v-list-item-avatar>
                        <v-avatar size="32" color="primary">
                          <span class="white--text caption">{{ employee.nombre.charAt(0) }}</span>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="body-2">{{ employee.nombre }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ employee.cargo }} - 
                          {{ employee.monthDays }} días en {{ selectedMonthName }}
                          <span v-if="employee.hasUnapprovedDays" class="warning--text">
                            ({{ employee.monthDaysUnapproved }} sin aprobar)
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <div class="d-flex align-center">
                          <v-chip small :color="getChipColor(employee.monthDays)">
                            {{ employee.monthDays }}
                          </v-chip>
                          <v-icon 
                            v-if="employee.hasUnapprovedDays"
                            color="warning"
                            size="16"
                            class="ml-1"
                            :title="`${employee.monthDaysUnapproved} días sin aprobar en ${selectedMonthName}`"
                          >
                            fa-solid fa-triangle-exclamation
                          </v-icon>
                        </div>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Calendario del mes -->
            <v-card outlined>
              <v-card-title class="pb-2">
                <v-icon class="mr-2">mdi-calendar-range</v-icon>
                Vista de Calendario - {{ selectedMonthName }} {{ selectedYear }}
              </v-card-title>
              
              <v-card-text>
                <v-calendar
                  ref="monthCalendar"
                  v-model="monthCalendarFocus"
                  :events="getMonthCalendarEvents()"
                  :event-color="getEventColor"
                  :event-margin-bottom="0"
                  :now="today"
                  :weekdays="weekday"
                  
                  type="month"
                 
                  :event-overlap-threshold="0"
                  @click:event="showEvent"
                >
                </v-calendar>
                
                <!-- Leyenda -->
                <div class="calendar-legend mt-3">
                  <v-chip small class="mr-2">
                    <div class="legend-indicator primary-indicator mr-1"></div>
                    Día completo
                  </v-chip>
                  <v-chip small class="mr-2">
                    <div class="legend-indicator half-indicator mr-1"></div>
                    Medio día
                  </v-chip>
                  <v-chip small class="mr-2">
                    <div class="legend-indicator out-indicator mr-1"></div>
                    Sin Aprobar
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="monthDialogVisible = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Menu/Popover para detalles de eventos -->
    <v-menu
      v-model="selectedOpen"
      :close-on-content-click="false"
      :activator="selectedElement"
      offset-x
    >
      <v-card
        color="grey lighten-4"
        min-width="300px"
        flat
      >
        <v-toolbar
          :color="selectedEvent.color || '#2196F3'"
          dark
          dense
        >
          <v-icon left>{{ getEventIcon(selectedEvent) }}</v-icon>
          <v-toolbar-title class="subtitle-1">
            {{ selectedEvent.name || 'Evento de Vacación' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="selectedOpen = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-3">
          <div v-if="selectedEvent.details">
            <!-- Información del colaborador -->
            <div class="mb-3">
              <v-chip
                small
                :color="selectedEvent.color || '#2196F3'"
                text-color="white"
                class="mb-2"
              >
                <v-icon left x-small>mdi-account</v-icon>
                {{ selectedEvent.details.employee }}
              </v-chip>
              
              <div v-if="selectedEvent.details.cargo" class="text-caption grey--text">
                <v-icon x-small class="mr-1">mdi-briefcase</v-icon>
                {{ selectedEvent.details.cargo }}
              </div>
            </div>

            <!-- Información del período -->
            <v-divider class="my-2"></v-divider>
            
            <div class="mb-2">
              <div class="d-flex align-center mb-1">
                <v-icon small color="primary" class="mr-2">mdi-calendar-range</v-icon>
                <span class="font-weight-medium">Período</span>
              </div>
              <div class="text-body-2 ml-6">
                {{ selectedEvent.details.periodo }}
              </div>
            </div>

            <!-- Tipo de vacación -->
            <div class="mb-2">
              <div class="d-flex align-center mb-1">
                <v-icon small color="success" class="mr-2">{{ getTipoIcon(selectedEvent.details.tipo) }}</v-icon>
                <span class="font-weight-medium">Tipo</span>
              </div>
              <div class="text-body-2 ml-6">
                {{ getTipoLabel(selectedEvent.details.tipo) }}
              </div>
            </div>

            <!-- Días tomados y estado de aprobación -->
            <div class="mb-2">
              <div class="d-flex align-center mb-1">
                <v-icon small color="warning" class="mr-2">mdi-clock-outline</v-icon>
                <span class="font-weight-medium">Duración</span>
              </div>
              <div class="text-body-2 ml-6">
                <div class="d-flex align-center">
                  <span class="mr-2">{{ formatDays(selectedEvent.details.diasSinAprobar>0?
                                        selectedEvent.details.diasSinAprobar:selectedEvent.details.diasTomados) }}</span>
                  <v-chip 
                    v-if="selectedEvent.details.diasSinAprobar && selectedEvent.details.diasSinAprobar > 0"
                    x-small
                    color="warning"
                    text-color="white"
                  >
                    <v-icon left x-small>fa-solid fa-triangle-exclamation</v-icon>
                    Sin aprobar
                  </v-chip>
                  <v-chip 
                    v-else-if="selectedEvent.details.diasTomados > 0"
                    x-small
                    color="success"
                    text-color="white"
                  >
                    <v-icon left x-small>mdi-check</v-icon>
                    Aprobado
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Eventos ocultos si es un resumen -->
            <div v-if="selectedEvent && selectedEvent.details && selectedEvent.details.summary && selectedEvent.details.hiddenEvents">
              <v-divider class="my-3"></v-divider>
              
              <div class="mb-2">
                <div class="d-flex align-center mb-2">
                  <v-icon small color="info" class="mr-2">mdi-account-group</v-icon>
                  <span class="font-weight-medium">Otros colaboradores este día</span>
                </div>
                
                <div class="ml-6">
                  <v-chip
                    v-for="(hiddenEvent, index) in selectedEvent.details.hiddenEvents.slice(0, 3)"
                    :key="index"
                    x-small
                    class="mr-1 mb-1"
                    :color="getCalendarEventColor(hiddenEvent.details.employee, hiddenEvent.details.tipo)"
                    text-color="white"
                  >
                    {{ getShortEmployeeName(hiddenEvent.details.employee) }}
                  </v-chip>
                  
                  <div v-if="selectedEvent.details.hiddenEvents.length > 3" class="text-caption grey--text mt-1">
                    y {{ selectedEvent.details.hiddenEvents.length - 3 }} más...
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-3 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="selectedOpen = false"
          >
            Cerrar
          </v-btn>
         
        </v-card-actions>
      </v-card>
    </v-menu>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'VacacionesAnuales',
  
  data() {
    return {
      codigoSac: this.$store.state.usuario.codigoSac,
      selectedYear: new Date().getFullYear(),
      selectedLevel: '1',
      searchText: '',
      expanded: [],
      loading: false, // Estado de carga para la API
      
      // Propiedades para el calendario de Vuetify
      calendarFocus: '',
      today: new Date().toISOString().substr(0, 10),
      
      // Años disponibles para el selector
      //en base al año actual, generamos 5 años hacia atrás y uno hacia adelante
     
      availableYears: Array.from(
        { length: 8 }, // 5 años atrás + año actual + 1 año adelante = 7 años
        (_, i) => (new Date().getFullYear()) - 6 + i
      ),
      //Niveles disponibles
      availableLevels: ['1', '2', '3', '4', '5'],
      weekday: [1, 2, 3, 4, 5],
      
      // Propiedades para el diálogo del calendario mensual
      monthDialogVisible: false,
      selectedMonth: '',
      selectedMonthName: '',
      selectedMonthIndex: -1,
      monthCalendarFocus: '',
      
      // Propiedades para el sistema de eventos click de Vuetify Calendar
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      
      // Paleta de colores para eventos
      eventColors: [
        '#2196F3', // Azul
        '#4CAF50', // Verde
        '#FF9800', // Naranja
        '#9C27B0', // Púrpura
        '#F44336', // Rojo
        '#00BCD4', // Cian
        '#795548', // Marrón
        '#607D8B', // Azul gris
        '#E91E63', // Rosa
        '#3F51B5', // Índigo
        '#FF5722', // Naranja profundo
        '#009688', // Verde azulado
        '#FFC107', // Ámbar
        '#673AB7', // Púrpura profundo
        '#8BC34A', // Verde claro
        '#CDDC39', // Lima
        '#FF6F61', // Coral
        '#6A4C93', // Violeta
        '#1ABC9C', // Turquesa
        '#E67E22', // Calabaza
      ],
      
      // Headers de la tabla
      tableHeaders: [
        { text: 'Colaborador', value: 'nombre', sortable: true, width: '280px' },
        { 
          text: 'Enero', 
          value: 'enero', 
          align: 'center', 
          width: '80px',
          month: 'enero',
          monthIndex: 0
        },
        { 
          text: 'Feb', 
          value: 'febrero', 
          align: 'center', 
          width: '80px',
          month: 'febrero',
          monthIndex: 1
        },
        { 
          text: 'Mar', 
          value: 'marzo', 
          align: 'center', 
          width: '80px',
          month: 'marzo',
          monthIndex: 2
        },
        { 
          text: 'Abr', 
          value: 'abril', 
          align: 'center', 
          width: '80px',
          month: 'abril',
          monthIndex: 3
        },
        { 
          text: 'May', 
          value: 'mayo', 
          align: 'center', 
          width: '80px',
          month: 'mayo',
          monthIndex: 4
        },
        { 
          text: 'Jun', 
          value: 'junio', 
          align: 'center', 
          width: '80px',
          month: 'junio',
          monthIndex: 5
        },
        { 
          text: 'Jul', 
          value: 'julio', 
          align: 'center', 
          width: '80px',
          month: 'julio',
          monthIndex: 6
        },
        { 
          text: 'Ago', 
          value: 'agosto', 
          align: 'center', 
          width: '80px',
          month: 'agosto',
          monthIndex: 7
        },
        { 
          text: 'Sep', 
          value: 'septiembre', 
          align: 'center', 
          width: '80px',
          month: 'septiembre',
          monthIndex: 8
        },
        { 
          text: 'Oct', 
          value: 'octubre', 
          align: 'center', 
          width: '80px',
          month: 'octubre',
          monthIndex: 9
        },
        { 
          text: 'Nov', 
          value: 'noviembre', 
          align: 'center', 
          width: '80px',
          month: 'noviembre',
          monthIndex: 10
        },
        { 
          text: 'Dic', 
          value: 'diciembre', 
          align: 'center', 
          width: '80px',
          month: 'diciembre',
          monthIndex: 11
        },
        { text: 'Total', value: 'totalTomado', align: 'center', width: '100px' },
        { text: 'Saldo', value: 'saldoRestante', align: 'center', width: '100px' },
        { text: '% Uso', value: 'porcentajeUso', align: 'center', width: '150px' }
      ],
      
      // Datos de colaboradores - Se cargan desde la API
      employeesData: [],
      
      // Cache de eventos detallados por empleado (carga lazy al expandir)
      employeeEventsCache: {},
      // Estado de carga de eventos por empleado
      loadingEvents: {}
    }
  },
  
  computed: {
    // Colaboradores procesados con cálculos de vacaciones (usa resúmenes del backend)
    processedEmployees() {
      return this.employeesData.map(employee => {
        // Los datos mensuales ya vienen pre-calculados del backend
        const diasPorMes = employee.diasPorMes || {
          enero: 0, febrero: 0, marzo: 0, abril: 0, mayo: 0, junio: 0,
          julio: 0, agosto: 0, septiembre: 0, octubre: 0, noviembre: 0, diciembre: 0
        }
        const diasSinAprobarPorMes = employee.diasSinAprobarPorMes || {
          enero: 0, febrero: 0, marzo: 0, abril: 0, mayo: 0, junio: 0,
          julio: 0, agosto: 0, septiembre: 0, octubre: 0, noviembre: 0, diciembre: 0
        }
        
        const totalTomado = employee.totalTomado !== undefined 
          ? employee.totalTomado 
          : Object.values(diasPorMes).reduce((sum, days) => sum + days, 0)
        const totalSinAprobar = employee.totalSinAprobar !== undefined 
          ? employee.totalSinAprobar 
          : Object.values(diasSinAprobarPorMes).reduce((sum, days) => sum + days, 0)
        const totalCombinado = totalTomado + totalSinAprobar
        
        const saldoRestante = employee.saldoAnual - totalCombinado
        const porcentajeUso = employee.saldoAnual > 0 ? (totalCombinado / employee.saldoAnual) * 100 : 0

        // Obtener eventos del cache si están disponibles
        const eventos = this.employeeEventsCache[employee.id] || employee.eventos || []

        return {
          ...employee,
          diasPorMes,
          diasSinAprobarPorMes,
          totalTomado,
          totalSinAprobar,
          totalCombinado,
          saldoRestante,
          porcentajeUso: Math.min(100, porcentajeUso),
          eventos,
          cantidadEventos: employee.cantidadEventos || eventos.length
        }
      })
    },
    
    // Colaboradores filtrados por búsqueda
    filteredEmployees() {
      if (!this.searchText) return this.processedEmployees
      
      const search = this.searchText.toLowerCase()
      return this.processedEmployees.filter(employee => 
        employee.nombre.toLowerCase().includes(search) ||
        employee.cargo.toLowerCase().includes(search) ||
        (employee.codigo && employee.codigo.toString().toLowerCase().includes(search))
      )
    },
    
    // Totales por mes
    totalesPorMes() {
      const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
      
      const totales = {}
      meses.forEach(mes => {
        // Sumar tanto días aprobados como sin aprobar
        totales[mes] = this.filteredEmployees.reduce((sum, emp) => {
          const diasAprobados = emp.diasPorMes[mes] || 0
          const diasSinAprobar = emp.diasSinAprobarPorMes[mes] || 0
          return sum + diasAprobados + diasSinAprobar
        }, 0)
      })
      
      return totales
    },
    
    // Meses que tienen días sin aprobar
    mesesConDiasSinAprobar() {
      const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
      
      const mesesConSinAprobar = {}
      meses.forEach(mes => {
        const tieneDiasSinAprobar = this.filteredEmployees.some(emp => 
          (emp.diasSinAprobarPorMes[mes] || 0) > 0
        )
        mesesConSinAprobar[mes] = tieneDiasSinAprobar
      })
      
      return mesesConSinAprobar
    },
    
    // Estadísticas generales
    totalEmpleados() {
      return this.filteredEmployees.length
    },
    
    totalDiasTomados() {
      return this.filteredEmployees.reduce((sum, emp) => sum + emp.totalCombinado, 0)
    },

    totalDiasSinAprobar() {
      return this.filteredEmployees.reduce((sum, emp) => sum + emp.totalSinAprobar, 0)
    },
    
    totalDiasDisponibles() {
      return this.filteredEmployees.reduce((sum, emp) => sum + emp.saldoAnual, 0)
    },
    
    promedioUso() {
      if (this.filteredEmployees.length === 0) return 0
      const sumaUso = this.filteredEmployees.reduce((sum, emp) => sum + emp.porcentajeUso, 0)
      return sumaUso / this.filteredEmployees.length
    }
  },
  
  methods: {
    // Formatea la fecha de ingreso para mostrarla en formato amigable
    formatFechaIngreso(fecha) {
      if (!fecha) return 'N/A'
      
      try {
        const date = new Date(fecha)
        if (isNaN(date.getTime())) return 'N/A'
        
        return date.toLocaleDateString('es-CO', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        })
      } catch (error) {
        return 'N/A'
      }
    },

    // Abre el diálogo del calendario mensual
    openMonthDialog(month, monthIndex, monthText) {
      this.selectedMonth = month
      this.selectedMonthIndex = monthIndex
      this.selectedMonthName = monthText
      
      // Configurar el foco del calendario en el mes seleccionado
      const monthDate = new Date(this.selectedYear, monthIndex, 1)
      this.monthCalendarFocus = monthDate.toISOString().substr(0, 10)
      
      this.monthDialogVisible = true
    },

    // Obtiene el total combinado (aprobados + sin aprobar) para un empleado en un mes específico
    getTotalCombinedForMonth(employee, mes) {
      const diasAprobados = employee.diasPorMes[mes] || 0
      const diasSinAprobar = employee.diasSinAprobarPorMes[mes] || 0
      return diasAprobados + diasSinAprobar
    },

    // Verifica si hay días sin aprobar para un empleado en un mes específico
    hasUnapprovedDaysInMonth(employee, mes) {
      return (employee.diasSinAprobarPorMes[mes] || 0) > 0
    },

    // Obtiene un color único para un evento basado en el colaborador y tipo
    getEventColorByEmployee(employeeName, eventType) {
      // Crear un hash simple basado en el nombre del colaborador
      let hash = 0
      for (let i = 0; i < employeeName.length; i++) {
        const char = employeeName.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convertir a 32bit integer
      }
      
      // Usar valor absoluto y módulo para obtener índice de color
      const colorIndex = Math.abs(hash) % this.eventColors.length
      return this.eventColors[colorIndex]
    },

    // Obtiene un color específico según el tipo de vacación
    getColorByVacationType(tipo) {
      const typeColors = {
        'completo': '#38bf4d',        // Azul - día completo
        'medio_dia_mañana': '#326cf5', // Naranja - medio día mañana
        'medio_dia_tarde': '#326cf5',  // Naranja profundo - medio día tarde
        'medio_dia': '#326cf5',        // Naranja - medio día genérico
        'sin_aprobar': '#f6b93a'     // Púrpura - personalizado
      }
      return typeColors[tipo] || '#2196F3' // Azul por defecto (día completo)
    },

    // Obtiene un color basado en la duración de las vacaciones
    getColorByDuration(dias) {
      if (dias <= 1) return '#FFC107'      // Ámbar - vacaciones cortas
      if (dias <= 3) return '#2196F3'      // Azul - vacaciones medias
      if (dias <= 7) return '#4CAF50'      // Verde - vacaciones largas
      if (dias <= 15) return '#9C27B0'     // Púrpura - vacaciones muy largas
      return '#F44336'                     // Rojo - vacaciones extensas
    },

    // Método principal para obtener color del evento
    getCalendarEventColor(employeeName, eventType, duration = 1) {
      // Usar color basado en tipo de vacación para mejor diferenciación visual
      return this.getColorByVacationType(eventType)
    },

    // Obtiene estadísticas de vacaciones del mes seleccionado
    getMonthVacationStats() {
      if (!this.selectedMonth) {
        return { totalEmployees: 0, totalDays: 0, totalPeriods: 0 }
      }

      const employeesWithVacations = this.filteredEmployees.filter(emp => 
        (emp.diasPorMes[this.selectedMonth] > 0) || (emp.diasSinAprobarPorMes[this.selectedMonth] > 0)
      )

      const totalDays = employeesWithVacations.reduce((sum, emp) => {
        const diasAprobados = emp.diasPorMes[this.selectedMonth] || 0
        const diasSinAprobar = emp.diasSinAprobarPorMes[this.selectedMonth] || 0
        return sum + diasAprobados + diasSinAprobar
      }, 0)

      const totalPeriods = employeesWithVacations.reduce((sum, emp) => {
        // Contar períodos desde el cache de eventos si está disponible
        const eventos = this.employeeEventsCache[emp.id] || emp.eventos || []
        const periodsInMonth = eventos.filter(evento => {
          const startDate = new Date(evento.start + 'T00:00:00')
          const endDate = new Date(evento.end + 'T00:00:00')
          return startDate.getMonth() === this.selectedMonthIndex || 
                 endDate.getMonth() === this.selectedMonthIndex ||
                 (startDate.getMonth() < this.selectedMonthIndex && endDate.getMonth() > this.selectedMonthIndex)
        }).length
        return sum + periodsInMonth
      }, 0)

      return {
        totalEmployees: employeesWithVacations.length,
        totalDays: this.formatNumber(totalDays), // Usar formatNumber para máximo 2 decimales
        totalPeriods
      }
    },

    // Obtiene colaboradores con vacaciones en el mes seleccionado
    getEmployeesWithVacationsInMonth() {
      if (!this.selectedMonth) return []

      return this.filteredEmployees
        .filter(emp => (emp.diasPorMes[this.selectedMonth] > 0) || (emp.diasSinAprobarPorMes[this.selectedMonth] > 0))
        .map(emp => {
          const diasAprobados = emp.diasPorMes[this.selectedMonth] || 0
          const diasSinAprobar = emp.diasSinAprobarPorMes[this.selectedMonth] || 0
          const totalDiasMes = diasAprobados + diasSinAprobar
          
          return {
            ...emp,
            monthDays: this.formatNumber(totalDiasMes), // Total combinado para el mes
            monthDaysApproved: this.formatNumber(diasAprobados), // Días aprobados
            monthDaysUnapproved: this.formatNumber(diasSinAprobar), // Días sin aprobar
            hasUnapprovedDays: diasSinAprobar > 0 // Indicador booleano
          }
        })
        .sort((a, b) => b.monthDays - a.monthDays) // Ordenar por días descendente
    },

    // Genera eventos del calendario para el mes seleccionado
    getMonthCalendarEvents() {
      if (!this.selectedMonth || this.selectedMonthIndex === -1) return []

      const events = []
      
      this.filteredEmployees.forEach(employee => {
        const eventos = this.employeeEventsCache[employee.id] || employee.eventos || []
        if (eventos.length > 0) {
          eventos.forEach(evento => {
            const startDate = new Date(evento.start + 'T00:00:00')
            const endDate = new Date(evento.end + 'T00:00:00')
            
            // Verificar si el evento se intersecta con el mes seleccionado
            const eventStartsInMonth = startDate.getMonth() === this.selectedMonthIndex && startDate.getFullYear() === this.selectedYear
            const eventEndsInMonth = endDate.getMonth() === this.selectedMonthIndex && endDate.getFullYear() === this.selectedYear
            const eventSpansMonth = startDate.getMonth() < this.selectedMonthIndex && endDate.getMonth() > this.selectedMonthIndex && 
                                   startDate.getFullYear() === this.selectedYear && endDate.getFullYear() === this.selectedYear
            
            if (eventStartsInMonth || eventEndsInMonth || eventSpansMonth) {
              // Ajustar las fechas para que estén dentro del mes seleccionado
              const monthStart = new Date(this.selectedYear, this.selectedMonthIndex, 1)
              const monthEnd = new Date(this.selectedYear, this.selectedMonthIndex + 1, 0)
              
              const eventStart = startDate < monthStart ? monthStart : startDate
              const eventEnd = endDate > monthEnd ? monthEnd : endDate
              
              const tipo = evento.tipo || 'completo'
              const diasTomados = evento.diasTomados !== null && evento.diasTomados !== undefined 
                ? evento.diasTomados 
                : this.calculateDays(evento.start, evento.end, tipo)

              const eventColor = this.getCalendarEventColor(employee.nombre, tipo, diasTomados)

              events.push({
                name: `${this.getShortEmployeeName(employee.nombre)} - ${this.getTipoLabel(tipo)}`,
                start: eventStart.toISOString().substr(0, 10),
                end: eventEnd.toISOString().substr(0, 10),
                color: eventColor,
                timed: false,
                details: {
                  employee: employee.nombre,
                  cargo: employee.cargo,
                  tipo: tipo,
                  diasTomados: diasTomados,
                  diasSinAprobar: evento.diasSinAprobar || 0,
                  periodo: `${evento.start} - ${evento.end}`  
                }
              })
            }
          })
        }
      })
      
      return this.groupEventsByDate(events)
    },    // Agrupa eventos por fecha para mejor visualización
    groupEventsByDate(events) {
      const grouped = {}
      
      events.forEach(event => {
        const date = event.start
        if (!grouped[date]) {
          grouped[date] = []
        }
        grouped[date].push(event)
      })
      
      // Crear eventos agrupados con indicador de cantidad
      const processedEvents = []
      
      Object.keys(grouped).forEach(date => {
        const dayEvents = grouped[date]
        
        if (dayEvents.length === 1) {
          // Un solo evento, mostrarlo normal
          processedEvents.push(dayEvents[0])
        } else if (dayEvents.length <= 3) {
          // Pocos eventos, mostrar todos
          dayEvents.forEach(event => processedEvents.push(event))
        } else {
          // Muchos eventos, mostrar los primeros 2 y un resumen
          processedEvents.push(dayEvents[0])
          processedEvents.push(dayEvents[1])
          
          // Crear evento resumen
          const remainingCount = dayEvents.length - 2
          const summaryEvent = {
            name: `${remainingCount} more`,
            start: date,
            end: date,
            color: '#9E9E9E', // Gris
            timed: false,
            details: {
              employee: 'Multiple employees',
              tipo: 'summary',
              diasTomados: this.formatNumber(dayEvents.slice(2).reduce((sum, e) => sum + e.details.diasTomados, 0)),
              periodo: date,
              summary: true,
              hiddenEvents: dayEvents.slice(2)
            }
          }
          processedEvents.push(summaryEvent)
        }
      })
      
      return processedEvents
    },

    // Calcula los días de vacaciones por mes para un colaborador
    calculateVacationDaysByMonth(eventos) {
      const diasPorMes = {
        enero: 0, febrero: 0, marzo: 0, abril: 0, mayo: 0, junio: 0,
        julio: 0, agosto: 0, septiembre: 0, octubre: 0, noviembre: 0, diciembre: 0
      }
      
      // Días sin aprobar por mes
      const diasSinAprobarPorMes = {
        enero: 0, febrero: 0, marzo: 0, abril: 0, mayo: 0, junio: 0,
        julio: 0, agosto: 0, septiembre: 0, octubre: 0, noviembre: 0, diciembre: 0
      }
      
      const mesesNombres = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ]
      
      
      eventos.forEach((evento, index) => {
        
        // Filtrar el evento para que solo incluya los días del año seleccionado
        const eventoFiltrado = this.filterEventByYear(evento, this.selectedYear)
        
        if (!eventoFiltrado) {
          return
        }
        
        // Si viene el valor exacto de días tomados desde la API, lo usamos
        if (evento.diasTomados !== undefined && evento.diasTomados !== null) {
          // Para eventos con diasTomados, lo distribuimos proporcionalmente
          // por los meses que abarca el período filtrado
          this.distributeVacationDaysByMonth(eventoFiltrado, diasPorMes, mesesNombres, evento.diasTomados)
        } else {
          // Fallback al método anterior para casos sin diasTomados
          this.calculateVacationByDateRange(eventoFiltrado, diasPorMes, mesesNombres)
        }
        
        // Procesar días sin aprobar si existen
        if (evento.diasSinAprobar !== undefined && evento.diasSinAprobar !== null && evento.diasSinAprobar > 0) {
          this.distributeVacationDaysByMonth(eventoFiltrado, diasSinAprobarPorMes, mesesNombres, evento.diasSinAprobar)
        }
        
      })
      
      // Retornar tanto días aprobados como sin aprobar
      return { diasPorMes, diasSinAprobarPorMes }
    },
    
    // Distribuye los días de vacaciones por mes usando el valor exacto de la API
    distributeVacationDaysByMonth(evento, diasPorMes, mesesNombres, diasTomadosOriginales = null) {
      const startDate = new Date(evento.start + 'T00:00:00')
      const endDate = new Date(evento.end + 'T00:00:00')
      const diasTomados = diasTomadosOriginales !== null ? diasTomadosOriginales : (evento.diasTomados || 0)
      const tipo = evento.tipo || 'completo'
      
      // Caso especial: evento de un solo día (especialmente medios días)
      if (evento.start === evento.end) {
        const month = startDate.getMonth()
        const mesNombre = mesesNombres[month]
        if (diasPorMes[mesNombre] !== undefined) {
          // Para eventos filtrados de un día, usar el valor proporcional si es necesario
          const diasAAsignar = diasTomadosOriginales !== null ? diasTomadosOriginales : diasTomados
          diasPorMes[mesNombre] += diasAAsignar
        }
        return
      }
      
      // Para rangos de fechas múltiples, calcular proporcionalmente basado en el rango filtrado
      // Calcular el número de días laborables en el rango filtrado
      let diasLaborablesEnRangoFiltrado = 0
      const diasLaborablesPorMes = {}
      
      // Inicializar contadores por mes
      mesesNombres.forEach(mes => {
        diasLaborablesPorMes[mes] = 0
      })
      
      // Contar días laborables por mes en el rango filtrado
      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay()
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Solo días laborables
          const month = currentDate.getMonth()
          const mesNombre = mesesNombres[month]
          if (diasLaborablesPorMes[mesNombre] !== undefined) {
            diasLaborablesPorMes[mesNombre]++
            diasLaborablesEnRangoFiltrado++
          }
        }
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      // Distribuir proporcionalmente los días tomados solo en el rango filtrado
      if (diasLaborablesEnRangoFiltrado > 0) {
        // Si tenemos el valor original de días tomados, calcular la proporción que corresponde al año
        let diasADistribuir = diasTomados
        
        if (diasTomadosOriginales !== null) {
          // Calcular qué proporción del período original está en el año seleccionado
          // Esto es una aproximación - en casos complejos se podría hacer más preciso
          diasADistribuir = diasTomadosOriginales * (diasLaborablesEnRangoFiltrado / Math.max(diasLaborablesEnRangoFiltrado, 1))
        }
        
        mesesNombres.forEach(mes => {
          if (diasLaborablesPorMes[mes] > 0) {
            const proporcion = diasLaborablesPorMes[mes] / diasLaborablesEnRangoFiltrado
            const diasAsignados = diasADistribuir * proporcion
            diasPorMes[mes] += diasAsignados
          }
        })
      }
      
    },
    
    // Método de cálculo anterior para casos sin diasTomados
    calculateVacationByDateRange(evento, diasPorMes, mesesNombres) {
      const startDate = new Date(evento.start + 'T00:00:00')
      const endDate = new Date(evento.end + 'T00:00:00')
      const tipoEvento = evento.tipo || 'completo'
      
      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay()
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          const month = currentDate.getMonth()
          const mesNombre = mesesNombres[month]
          if (diasPorMes[mesNombre] !== undefined) {
            let valorDia = 1
            if (tipoEvento === 'medio_dia_mañana' || tipoEvento === 'medio_dia_tarde') {
              valorDia = 0.5
            }
            diasPorMes[mesNombre] += valorDia
          }
        }
        currentDate.setDate(currentDate.getDate() + 1)
      }
    },
    
    // Calcula la cantidad de días entre dos fechas considerando diasTomados de la API
    calculateDays(startDate, endDate, tipo = 'completo', diasTomados = null) {
      // Si tenemos el valor exacto desde la API, lo usamos
      if (diasTomados !== null && diasTomados !== undefined) {
        return diasTomados
      }
      
      // Fallback al cálculo anterior
      const start = new Date(startDate + 'T00:00:00')
      const end = new Date(endDate + 'T00:00:00')
      let days = 0
      
      const currentDate = new Date(start)
      while (currentDate <= end) {
        const dayOfWeek = currentDate.getDay()
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          if (tipo === 'medio_dia_mañana' || tipo === 'medio_dia_tarde') {
            days += 0.5
          } else {
            days += 1
          }
        }
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      return days
    },
    
    // Formatea un rango de fechas para mostrar
    formatDateRange(startDate, endDate) {
      // Crear fechas locales para evitar problemas de zona horaria
      const start = new Date(startDate + 'T00:00:00')
      const end = new Date(endDate + 'T00:00:00')
      
      const options = { day: 'numeric', month: 'short' }
      return `${start.toLocaleDateString('es-ES', options)} - ${end.toLocaleDateString('es-ES', options)}`
    },
    
    // Determina el color del chip según la cantidad de días (más días = mejor)
    getChipColor(days) {
      if (days === 0) return 'error'
      if (days <= 5) return 'warning'
      if (days <= 10) return 'success'
      return 'success'
    },
    
    // Determina el color del texto del chip
    getTextColor(days) {
      return 'white'
    },
    
    // Determina el color de la barra de progreso (más uso = mejor)
    getProgressColor(percentage) {
      if (percentage <= 50) return 'error'
      if (percentage <= 80) return 'warning'
      return 'success'
    },
    
    // Obtiene la etiqueta descriptiva del tipo de vacación
    getTipoLabel(tipo) {
      const tipos = {
        'completo': 'Día completo',
        'medio_dia_mañana': 'Medio día (mañana)',
        'medio_dia_tarde': 'Medio día (tarde)'
      }
      return tipos[tipo] || 'Día completo'
    },
    
    // Obtiene el ícono según el tipo de vacación
    getTipoIcon(tipo) {
      const iconos = {
        'completo': 'mdi-calendar',
        'medio_dia_mañana': 'mdi-weather-sunny',
        'medio_dia_tarde': 'mdi-weather-sunset'
      }
      return iconos[tipo] || 'mdi-calendar'
    },
    
    // Formatea el número de días para mostrar (ej: 2.5 días)
    formatDays(days) {
      if (days === 0) return '0 días'
      if (days === 1) return '1 día'
      if (days % 1 === 0) return `${days} días`
      return `${days} días` // Para medios días como 0.5, 1.5, etc.
    },
    
    // Formatea números con máximo 2 decimales
    formatNumber(value) {
      if (typeof value !== 'number') return value
      if (value % 1 === 0) return value // Si es entero, devolver sin decimales
      return Math.round(value * 100) / 100 // Redondear a máximo 2 decimales
    },
    
    // Genera eventos para v-calendar de Vuetify basado en los eventos de vacaciones del colaborador
    getCalendarEvents(employee) {
      const events = []
      
      if (employee.eventos && employee.eventos.length > 0) {
        employee.eventos.forEach((evento, index) => {
          
          const tipo = evento.tipo || 'completo'
          const diasTomados = evento.diasSinAprobar > 0 ? evento.diasSinAprobar : (evento.diasTomados !== null && evento.diasTomados !== undefined
            ? evento.diasTomados
            : this.calculateDays(evento.start, evento.end, tipo))

          // Filtrar evento para que solo incluya las fechas del año seleccionado
          const eventoPorAnio = this.filterEventByYear(evento, this.selectedYear)
          
          // Solo crear el evento si tiene fechas válidas para el año seleccionado
          if (eventoPorAnio && eventoPorAnio.start && eventoPorAnio.end) {
            // Crear evento para Vuetify Calendar
            const eventColor = this.getCalendarEventColor(employee.nombre, tipo, diasTomados)
            
            events.push({
              name: `${this.getShortEmployeeName(employee.nombre)} (${this.formatDays(diasTomados)})`,
              start: eventoPorAnio.start,
              end: eventoPorAnio.end,
              color: eventColor,
              timed: false,
              details: {
                employee: employee.nombre,
                tipo: tipo,
                diasTomados: diasTomados,
                diasSinAprobar: evento.diasSinAprobar || 0,
                periodo: `${evento.start} - ${evento.end}`,
                periodoFiltrado: `${eventoPorAnio.start} - ${eventoPorAnio.end}`
              }
            })
            
          } else {
          }
        })
      }
      
      return events
    },
    
    // Filtra un evento de vacaciones para mostrar solo las fechas del año especificado
    filterEventByYear(evento, year) {
      const startDate = new Date(evento.start + 'T00:00:00')
      const endDate = new Date(evento.end + 'T00:00:00')
      
      // Definir el rango del año seleccionado
      const yearStart = new Date(year, 0, 1) // 1 de enero del año
      const yearEnd = new Date(year, 11, 31) // 31 de diciembre del año
      
      // Caso 1: El evento está completamente fuera del año seleccionado
      if (endDate < yearStart || startDate > yearEnd) {
        return null
      }
      
      // Caso 2: El evento está parcial o completamente dentro del año
      const filteredStart = startDate < yearStart ? yearStart : startDate
      const filteredEnd = endDate > yearEnd ? yearEnd : endDate
      
      const result = {
        start: filteredStart.toISOString().substr(0, 10),
        end: filteredEnd.toISOString().substr(0, 10),
        tipo: evento.tipo
      }
      
      return result
    },
    
    // Obtiene eventos para una fecha específica
    getEventsForDate(employee, date) {
      const events = this.getCalendarEvents(employee)
      return events.filter(event => {
        const eventStart = new Date(event.start)
        const eventEnd = new Date(event.end)
        const checkDate = new Date(date)
        return checkDate >= eventStart && checkDate <= eventEnd
      })
    },
    
    // Obtiene el color del evento
    getEventColor(event) {
      // Si el evento ya tiene un color personalizado, usarlo
      if (event.color && event.color.startsWith('#')) {
        return event.color
      }
      
      // Si es un color de Vuetify, usar el sistema de color basado en tipo de vacación
      if (event.details && event.details.tipo) {
        return this.getColorByVacationType(event.details.tipo)
      }
      
      return event.color || '#2196F3' // Azul por defecto
    },

    // Obtiene el nombre corto para mostrar en eventos
    getShortEmployeeName(fullName) {
      if (!fullName) return ''
      
      // Si el nombre es muy largo, usar solo el primer nombre y primer apellido
      const nameParts = fullName.trim().split(' ')
      if (nameParts.length >= 2 && fullName.length > 15) {
        return `${nameParts[0]} ${nameParts[1]}`
      }
      
      // Si aún es muy largo, usar solo el primer nombre
      if (fullName.length > 20) {
        return nameParts[0]
      }
      
      return fullName
    },
    
    // Muestra detalles del evento
    showEventDetails(event) {
      // Si es un evento resumen, mostrar todos los eventos ocultos
      if (event.details.summary) {
        let message = `Eventos adicionales para ${event.start}:\n\n`
        event.details.hiddenEvents.forEach((hiddenEvent, index) => {
          message += `${index + 1}. ${hiddenEvent.details.employee}\n`
          message += `   Cargo: ${hiddenEvent.details.cargo || 'N/A'}\n`
          message += `   Tipo: ${this.getTipoLabel(hiddenEvent.details.tipo)}\n`
          message += `   Días: ${this.formatDays(hiddenEvent.details.diasTomados)}\n\n`
        })
        alert(message)
        return
      }

      // Crear mensaje más detallado para evento normal
      let message = `${event.details.employee}`
      
      if (event.details.cargo) {
        message += `\nCargo: ${event.details.cargo}`
      }
      
      message += `\nPeríodo: ${event.details.periodo}`
      message += `\nTipo: ${this.getTipoLabel(event.details.tipo)}`
      message += `\nDías: ${this.formatDays(event.details.diasTomados)}`
      
      alert(message)
    },
    
    // Método para manejar clicks en eventos del calendario Vuetify
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedOpen = true;
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 10);
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    
    // Cierra el diálogo de evento
    closeEventDialog() {
      this.selectedOpen = false;
      this.selectedEvent = null;
    },
    
    // Obtiene el ícono para un evento
    getEventIcon(event) {
      if (event.details && event.details.summary) {
        return 'mdi-calendar-multiple';
      }
      return 'mdi-calendar-heart';
    },
    
    // Formatea el período de display
    formatPeriodDisplay(event) {
      // Si es un string, devolverlo directamente
      if (typeof event === 'string') {
        return event;
      }
      
      // Si tiene propiedades start y end
      if (event && event.start && event.end) {
        if (event.start === event.end) {
          return event.start;
        }
        return `${event.start} - ${event.end}`;
      }
      
      // Si es un evento con detalles
      if (event && event.details && event.details.periodo) {
        return event.details.periodo;
      }
      
      return 'N/A';
    },
    
    // Ver detalles del colaborador
    viewEmployeeDetails(colaborador) {
      this.$store.dispatch('setSnackbar', {
        text: `Ver detalles de ${colaborador.colaborador}`,
        color: 'info',
        timeout: 3000
      });
    },
    
    // Navega a un mes específico en el calendario
    navigateToCalendarMonth(dateString) {
      try {
        // Parsear la fecha y obtener el primer día del mes
        const date = new Date(dateString)
        if (isNaN(date.getTime())) {
          return
        }
        
        // Crear la fecha del primer día del mes
        const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)
        
        // Actualizar el foco del calendario
        this.calendarFocus = firstDayOfMonth.toISOString().substr(0, 10)
        
        // Mostrar mensaje de confirmación
        this.$store.dispatch('setSnackbar', {
          text: `Navegando al ${this.getMonthName(date.getMonth())} ${date.getFullYear()}`,
          color: 'info',
          timeout: 2000
        })
      } catch (error) {
        this.$store.dispatch('setSnackbar', {
          text: 'Error al navegar al calendario',
          color: 'error'
        })
      }
    },
    
    // Obtiene el nombre del mes
    getMonthName(monthIndex) {
      const months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ]
      return months[monthIndex] || 'Desconocido'
    },
    
    // Carga los datos desde la API real
    loadData() {
      let me = this
      try {
        // Mostrar loading
        me.loading = true
        
        // Limpiar cache de eventos al recargar datos
        me.employeeEventsCache = {}
        me.loadingEvents = {}
        me.expanded = []
        
        // Obtener parámetros del usuario logueado desde el store
        const userInfo = me.$store.getters.getUserInfo || {}
        const nivel = this.selectedLevel || 1 // Valor por defecto
        const codigoEncargado = userInfo.codigoEncargado || me.codigoSac || '177' // Valor por defecto
        
        // Llamada a la API usando axios directo (patrón consistente)
        axios
          .get(`api/personas/GetEmpleadosVacacion/${me.selectedYear}/${nivel}/${codigoEncargado}/${false}`)
          .then(function (response) {
            console.log('Respuesta de la API:', response)
            // Verificar si la respuesta tiene la estructura esperada
            if (response.data && response.data.employeesData) {
              // Procesar los datos con resúmenes mensuales del backend
              me.employeesData = response.data.employeesData.map(employee => ({
                id: employee.id,
                nombre: employee.nombre || employee.nombreCompleto || 'Sin nombre',
                fechaIngreso: employee.fechaIngreso || 'Sin fecha',
                cargo: employee.cargo || employee.puesto || 'Sin cargo',
                saldoAnual: employee.saldoAnual || employee.diasVacacionesAnuales || 0,
                diasPorMes: employee.diasPorMes || {},
                diasSinAprobarPorMes: employee.diasSinAprobarPorMes || {},
                totalTomado: employee.totalTomado || 0,
                totalSinAprobar: employee.totalSinAprobar || 0,
                cantidadEventos: employee.cantidadEventos || 0,
                eventos: [] // Los eventos se cargan bajo demanda al expandir
              }))
              
              // Mostrar mensaje de éxito
              me.$store.dispatch('setSnackbar', {
                text: `Datos de vacaciones ${me.selectedYear} cargados correctamente`,
                color: 'success',
                timeout: 3000
              })
            } else {
              // Usar datos de ejemplo si la estructura no es la esperada
              me.useFallbackData()
            }
          })
          .catch(function (error) {
            
            // Mostrar error específico según el tipo
            let errorMessage = 'Error al cargar los datos de vacaciones'
            
            if (error.response) {
              // Error de respuesta del servidor
              if (error.response.status === 404) {
                errorMessage = 'No se encontraron datos de vacaciones para los parámetros especificados'
              } else if (error.response.status === 403) {
                errorMessage = 'No tienes permisos para acceder a estos datos'
              } else if (error.response.status >= 500) {
                errorMessage = 'Error interno del servidor. Intenta más tarde.'
              }
            } else if (error.request) {
              // Error de red
              errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
            }
            
            me.$store.dispatch('setSnackbar', {
              text: errorMessage,
              color: 'error',
              timeout: 5000
            })
            
            // Usar datos de ejemplo en caso de error
            me.useFallbackData()
          })
          .finally(function () {
            me.loading = false
          })
        
      } catch (error) {
        me.loading = false
        me.$store.dispatch('setSnackbar', {
          text: 'Error inesperado al cargar los datos',
          color: 'error'
        })
        me.useFallbackData()
      }
    },
    
    // Mapea tipos de vacación desde la API a nuestro formato interno
    mapTipoVacacion(tipoApi) {
      const mapeo = {
        'COMPLETO': 'completo',
        'MEDIO_DIA_MANANA': 'medio_dia_mañana',
        'MEDIO_DIA_TARDE': 'medio_dia_tarde',
        'PERSONALIZADO': 'personalizado',
        // Agregar más mapeos según sea necesario
        '1': 'completo',
        '2': 'medio_dia_mañana',
        '3': 'medio_dia_tarde'
      }
      
      return mapeo[tipoApi] || 'completo'
    },

    // Maneja la expansión de una fila para cargar eventos bajo demanda
    onItemExpanded({ item, value }) {
      if (value) {
        this.loadEmployeeEvents(item)
      }
    },

    // Carga los eventos detallados de un empleado bajo demanda
    loadEmployeeEvents(employee) {
      let me = this
      const employeeId = employee.id

      // Si ya están en cache, no volver a cargar
      if (me.employeeEventsCache[employeeId]) return

      // Marcar como cargando usando Vue.set para reactividad
      me.$set(me.loadingEvents, employeeId, true)

      axios
        .get(`api/personas/GetEventosEmpleado/${employeeId}/${me.selectedYear}/${false}`)
        .then(function (response) {
          const eventos = (response.data || []).map(evento => ({
            start: evento.start || evento.fechaInicio,
            end: evento.end || evento.fechaFin,
            tipo: evento.tipo || me.mapTipoVacacion(evento.tipoVacacion),
            diasTomados: evento.diasTomados !== null && evento.diasTomados !== undefined
              ? parseFloat(evento.diasTomados)
              : null,
            diasSinAprobar: evento.diasSinAprobar !== null && evento.diasSinAprobar !== undefined
              ? parseFloat(evento.diasSinAprobar)
              : 0
          }))
          me.$set(me.employeeEventsCache, employeeId, eventos)
        })
        .catch(function (error) {
          console.error('Error al cargar eventos del empleado:', error)
          me.$set(me.employeeEventsCache, employeeId, [])
          me.$store.dispatch('setSnackbar', {
            text: `Error al cargar detalle de vacaciones de ${employee.nombre}`,
            color: 'error',
            timeout: 3000
          })
        })
        .finally(function () {
          me.$set(me.loadingEvents, employeeId, false)
        })
    },
    
    // Usar datos de ejemplo como fallback
    useFallbackData() {
      // Mantener los datos de ejemplo para desarrollo/testing
      this.employeesData = [
        {
          id: 1,
          nombre: 'Ana Pérez (Datos de ejemplo)',
          cargo: 'Analista Senior',
          saldoAnual: 20,
          eventos: [
            { 
              start: '2025-02-05', 
              end: '2025-02-09', 
              tipo: 'completo',
              diasTomados: 5,
              diasSinAprobar: 0
            },
            { 
              start: '2025-08-10', 
              end: '2025-08-10', 
              tipo: 'medio_dia_tarde',
              diasTomados: 0,
              diasSinAprobar: 0.5
            },
            { 
              start: '2025-09-15', 
              end: '2025-09-17', 
              tipo: 'completo',
              diasTomados: 0,
              diasSinAprobar: 3
            }
          ]
        },
        {
          id: 2,
          nombre: 'Carlos Ruiz (Datos de ejemplo)',
          cargo: 'Desarrollador',
          saldoAnual: 15,
          eventos: [
            { 
              start: '2025-03-01', 
              end: '2025-03-03', 
              tipo: 'completo',
              diasTomados: 3,
              diasSinAprobar: 0
            },
            { 
              start: '2025-07-20', 
              end: '2025-07-21', 
              tipo: 'completo',
              diasTomados: 1,
              diasSinAprobar: 1
            }
          ]
        }
      ]
    }
  },
  
  mounted() {
    // Configurar el calendario para mostrar el mes actual
    this.calendarFocus = new Date().toISOString().substr(0, 10)
    
    // Actualizar título de la aplicación
    this.$store.dispatch('actualizarTitulo', 'Vacaciones Anuales')
    
    // Cargar datos al montar el componente
    this.loadData()
  }
}
</script>

<style scoped>
.vacation-timeline {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  min-height: 100px;
}

.vacation-event {
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}

.vacation-event:last-child {
  border-bottom: none;
}

.calendar-container {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .v-data-table >>> .v-data-table__wrapper {
    overflow-x: auto;
  }
  
  .v-chip {
    font-size: 10px !important;
    height: 20px !important;
  }
}

/* Header styling */
.v-data-table >>> thead th {
  background-color: #f5f5f5 !important;
  font-weight: 600 !important;
}

/* Progress bar styling */
.v-progress-linear >>> .v-progress-linear__content {
  font-size: 11px;
}


.event-chip:hover {
  cursor: pointer;
  opacity: 0.8;
}

/* Calendar legend */
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}

.legend-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.primary-indicator {
  background-color: #38bf4d;
}

.half-indicator {
  background-color: #326cf5;
}

.out-indicator {
  background-color: #f6b93a;
}

.calendar-section {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  margin-top: 16px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .vacation-calendar {
    font-size: 12px;
  }
  
  .event-chip {
    font-size: 8px !important;
    height: 16px !important;
  }
  
  .vacation-calendar >>> .v-calendar-weekly__day {
    min-height: 80px;
  }
}

/* Highlights for vacation periods */
.vacation-calendar >>> .vc-highlight {
  border-radius: 4px;
  margin: 1px;
}

.vacation-calendar >>> .vc-highlight-solid {
  opacity: 0.8;
}

.vacation-calendar >>> .vc-highlight-light {
  opacity: 0.3;
}

/* Popover styling */
.vacation-calendar >>> .vc-popover-content {
  background-color: #424242;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

/* Calendar legend */
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}

.legend-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.primary-indicator {
  background-color: #38bf4d;
}

.half-indicator {
  background-color: #326cf5;
}

.out-indicator {
  background-color: #f6b93a;
}

.calendar-section {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  margin-top: 16px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .vacation-calendar >>> .vc-container {
    font-size: 12px;
  }
  
  .vacation-calendar >>> .vc-day {
    min-height: 40px;
  }
  
  .vacation-calendar >>> .vc-dot {
    width: 6px;
    height: 6px;
  }
  
  .vacation-calendar >>> .vc-title {
    font-size: 16px;
  }
}

/* Headers clicables para meses */
.clickable-month-header {
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;
}

.clickable-month-header:hover {
  background-color: rgba(25, 118, 210, 0.1) !important;
}

.clickable-month-header:active {
  background-color: rgba(25, 118, 210, 0.2) !important;
}

.month-header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #1976d2;
}

/* Calendario del diálogo mensual */
.month-vacation-calendar {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  min-height: 400px;
}

.month-vacation-calendar >>> .v-calendar-daily_head-day {
  background-color: #81d4fa !important; /* Color celeste */
  color: #0277bd !important; /* Texto azul oscuro para contraste */
  font-weight: 600 !important;
  border-bottom: 1px solid #4fc3f7;
}

/* Cabeceras de días de la semana (Lu, Ma, Mi, etc.) */
.month-vacation-calendar >>> .v-calendar-daily_head-weekday {
  background-color: #4fc3f7 !important; /* Celeste más intenso */
  color: #01579b !important; /* Azul más oscuro */
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #29b6f6;
}

.month-vacation-calendar >>> .v-calendar-weekly__day {
  min-height: 80px;
}

/* Estilos mejorados para eventos del calendario */
.vacation-calendar >>> .v-event,
.month-vacation-calendar >>> .v-event {
  border-radius: 6px !important;
  font-weight: 500 !important;
  font-size: 11px !important;
  padding: 2px 6px !important;
  margin: 1px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2) !important;
  border: none !important;
  color: white !important;
  text-shadow: 0 1px 1px rgba(0,0,0,0.3) !important;
  transition: all 0.2s ease !important;
  cursor: pointer !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.vacation-calendar >>> .v-event:hover,
.month-vacation-calendar >>> .v-event:hover {
  transform: scale(1.02) !important;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3) !important;
  z-index: 10 !important;
}

/* Estilos específicos para diferentes tipos de días */
.vacation-calendar >>> .v-calendar-weekly__day,
.month-vacation-calendar >>> .v-calendar-weekly__day {
  border: 1px solid #f0f0f0 !important;
  background-color: #fafafa;
  transition: background-color 0.2s ease;
}

.vacation-calendar >>> .v-calendar-weekly__day:hover,
.month-vacation-calendar >>> .v-calendar-weekly__day:hover {
  background-color: #f5f5f5 !important;
}

/* Estilos para los números de los días */
.vacation-calendar >>> .v-calendar-weekly__day-label,
.month-vacation-calendar >>> .v-calendar-weekly__day-label {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #424242 !important;
  padding: 4px !important;
}

/* Estilos para el día actual */
.vacation-calendar >>> .v-calendar-weekly__day--today,
.month-vacation-calendar >>> .v-calendar-weekly__day--today {
  background-color: #e3f2fd !important;
}

.vacation-calendar >>> .v-calendar-weekly__day--today .v-calendar-weekly__day-label,
.month-vacation-calendar >>> .v-calendar-weekly__day--today .v-calendar-weekly__day-label {
  background-color: #2196F3 !important;
  color: white !important;
  border-radius: 50% !important;
  width: 28px !important;
  height: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Colores para múltiples eventos en el mismo día */
.vacation-calendar >>> .v-calendar-weekly__day-month .v-event,
.month-vacation-calendar >>> .v-calendar-weekly__day-month .v-event {
  margin-bottom: 2px !important;
}

/* Ajuste de header del calendario */
.vacation-calendar >>> .v-calendar-weekly__head,
.month-vacation-calendar >>> .v-calendar-weekly__head {
  background: #4fc3f7 !important; /* Color celeste sólido */
  color: #01579b !important; /* Azul oscuro */
}

.vacation-calendar >>> .v-calendar-weekly__head-weekday,
.month-vacation-calendar >>> .v-calendar-weekly__head-weekday {
  color: #01579b !important; /* Azul oscuro */
  font-weight: 600 !important;
  font-size: 13px !important;
  padding: 12px 8px !important;
}

/* Animación para eventos que aparecen */
@keyframes eventAppear {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.vacation-calendar >>> .v-event,
.month-vacation-calendar >>> .v-event {
  animation: eventAppear 0.3s ease-out !important;
}

/* Estilo especial para eventos de resumen */
.vacation-calendar >>> .v-event[data-summary="true"],
.month-vacation-calendar >>> .v-event[data-summary="true"] {
  background-color: #9E9E9E !important;
  font-style: italic !important;
  opacity: 0.8 !important;
  font-size: 10px !important;
}

.vacation-calendar >>> .v-event[data-summary="true"]:hover,
.month-vacation-calendar >>> .v-event[data-summary="true"]:hover {
  opacity: 1 !important;
  background-color: #757575 !important;
}

/* Indicadores de múltiples eventos en un día */
.vacation-calendar >>> .v-calendar-weekly__day[data-multiple-events="true"],
.month-vacation-calendar >>> .v-calendar-weekly__day[data-multiple-events="true"] {
  position: relative;
}

.vacation-calendar >>> .v-calendar-weekly__day[data-multiple-events="true"]::after,
.month-vacation-calendar >>> .v-calendar-weekly__day[data-multiple-events="true"]::after {
  content: '';
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background-color: #FF5722;
  border-radius: 50%;
  z-index: 5;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

/* Mejor espaciado para múltiples eventos */
.vacation-calendar >>> .v-calendar-weekly__day .v-event + .v-event,
.month-vacation-calendar >>> .v-calendar-weekly__day .v-event + .v-event {
  margin-top: 2px !important;
}

.calendar-event-item {
  cursor: pointer;
  font-size: 11px;
  padding: 2px 4px;
  border-radius: 3px;
  margin: 1px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-event-item:hover {
  opacity: 0.8;
  transform: scale(1.02);
  transition: all 0.2s ease;
}

/* Responsive para el diálogo */
@media (max-width: 600px) {
  .month-vacation-calendar {
    min-height: 300px;
  }
  
  .calendar-event-item {
    font-size: 9px;
    padding: 1px 2px;
  }
}
</style>
